import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserPlus, Loader2, AlertTriangle, CheckCircle2, Building2 } from "lucide-react";
import { useDistributor } from "../context/DistributorContext";

export default function DistributorRegisterPage() {
  const navigate = useNavigate();
  const { register } = useDistributor();
  const [form, setForm] = useState({
    email: "", password: "", company: "", contact_name: "", phone: "",
    kvk: "", vat_number: "", country: "Nederland", notes: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (form.password.length < 8) {
      setError("Wachtwoord moet minimaal 8 tekens lang zijn.");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Aanmelden mislukt — probeer het opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="mx-auto max-w-md px-5 py-12" data-testid="distributor-register-success">
        <div className="rounded-md border border-emerald-300 bg-emerald-50 p-8 text-center">
          <CheckCircle2 className="mx-auto text-emerald-600" size={36} />
          <h2 className="mt-3 font-display text-xl font-bold text-zinc-900">Aanmelding ontvangen</h2>
          <p className="mt-2 text-sm text-zinc-700">
            NIBRO beoordeelt uw aanvraag binnen <strong>1-2 werkdagen</strong> en
            laat u per e-mail weten zodra uw account geactiveerd is en uw opslag
            is bepaald.
          </p>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-nibro-sage-dark px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-white hover:bg-nibro-sage"
          >
            Naar de homepagina
          </button>
        </div>
      </div>
    );
  }

  const Field = ({ name, label, type = "text", required = false, autoComplete, minLength }) => (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-wide text-zinc-600">
        {label}{required && <span className="text-aramfix-red"> *</span>}
      </span>
      <input
        type={type}
        required={required}
        minLength={minLength}
        value={form[name]}
        onChange={(e) => upd(name, e.target.value)}
        data-testid={`dist-reg-${name}`}
        autoComplete={autoComplete}
        className="mt-1 w-full rounded border border-zinc-200 px-3 py-2 text-sm focus:border-nibro-sage-dark focus:outline-none"
      />
    </label>
  );

  return (
    <div className="mx-auto max-w-2xl px-5 py-10" data-testid="distributor-register-page">
      <div className="rounded-md border border-zinc-200 bg-white p-6 shadow-sm sm:p-8">
        <div className="mb-4 flex items-center gap-2">
          <Building2 className="text-nibro-sage-dark" size={22} />
          <h1 className="font-display text-2xl font-bold text-zinc-900">Distributeur aanmelding</h1>
        </div>
        <p className="mb-6 text-sm text-zinc-600">
          Vul onderstaand formulier in. Na goedkeuring krijgt u toegang tot uw
          eigen distributeur-prijzen op alle producten, uw order historie en uw
          persoonlijke prijslijst als Excel-export.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field name="company" label="Bedrijfsnaam" required />
            <Field name="contact_name" label="Contactpersoon" required />
            <Field name="email" label="E-mail" type="email" required autoComplete="email" />
            <Field name="phone" label="Telefoon" required />
            <Field name="kvk" label="KvK-nummer" />
            <Field name="vat_number" label="BTW-nummer" />
            <Field name="country" label="Land" />
            <Field name="password" label="Wachtwoord (min 8 tekens)" type="password" required minLength={8} autoComplete="new-password" />
          </div>
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wide text-zinc-600">
              Opmerking / project context (optioneel)
            </span>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => upd("notes", e.target.value)}
              data-testid="dist-reg-notes"
              className="mt-1 w-full rounded border border-zinc-200 px-3 py-2 text-sm focus:border-nibro-sage-dark focus:outline-none"
            />
          </label>
          {error && (
            <div
              className="flex items-start gap-2 rounded bg-aramfix-red/10 px-3 py-2 text-xs font-semibold text-aramfix-red"
              data-testid="dist-reg-error"
            >
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" /> <span>{error}</span>
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <Link to="/distributor/login" className="text-xs font-semibold text-nibro-sage-dark hover:underline">
              Reeds een account? Inloggen →
            </Link>
            <button
              type="submit"
              disabled={loading}
              data-testid="dist-reg-submit"
              className="inline-flex items-center gap-2 rounded-md bg-nibro-sage-dark px-6 py-3 text-sm font-bold uppercase tracking-wide text-white transition hover:bg-nibro-sage disabled:opacity-60"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
              Aanmelden
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
